import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Input,
  Label,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { useSelector } from 'react-redux';
import { AxiosAPI, getGroupID } from '../../../../../../helpers/AxiosAPI';
import { Colxx } from '../../../../../../components/common/CustomBootstrap';
import { NotificationManager } from '../../../../../../components/common/react-notifications';
import {
  API_Flexkeeping,
  API_Partner_Flexkeeping,
  API_WorkOrders,
} from '../../../../../../constants/APIs';
import { toTitleCase, formatToUTC } from '../../../../../../helpers/Utils';

const STATUS_OPTIONS = [
  'Submitted',
  'Acknowledged',
  'Completed',
  'Archived',
  'Cancelled',
];

const WORK_ORDER_TYPE_OPTIONS = [
  { title: 'Task', value: 'task' },
  { title: 'Repair', value: 'repair' },
  // { title: 'Room order', value: 'room_order' },
  { title: 'Flaw cleaning', value: 'flaw_cleaning' },
  // { title: 'Issue', value: 'issue' },
  { title: 'Feedback', value: 'feedback' },
];

// const workOrderStatuses = [
//   { id: 1, name: 'Submitted' },
//   { id: 2, name: 'Fixed' },
//   { id: 3, name: 'Approved' },
//   { id: 4, name: 'Done' },
//   { id: 5, name: 'Archived' },
//   { id: 7, name: 'Cancelled' },
// ];

const getStateId = (state) => {
  switch (state) {
    case 'Submitted':
      return 1;
    case 'Acknowledged':
      return 3;
    case 'Completed':
      return 4;
    case 'Archived':
      return 5;
    case 'Cancelled':
      return 7;
  }
};

export const getChannelIntegration = async (groupId, channel) => {
  const res = await AxiosAPI({
    method: 'get',
    url: `${API_Flexkeeping}/get/channel-integration?groupId=${groupId}&channel=${toTitleCase(channel)}`,
  });

  return res;
};

export default function WorkOrderModal({ visible, toggle, onSuccess, item }) {
  const isEdit = !!item;

  const [system, setSystem] = useState('Flexkeeping');
  const [facilityId, setFacilityId] = useState('');
  const [workOrderType, setWorkOrderType] = useState('');
  const [priority, setPriority] = useState(1);
  const [status, setStatus] = useState('Submitted');
  const [externalStatus, setExternalStatus] = useState('');
  const [roomTitle, setRoomTitle] = useState('');
  const [externalWorkOrderId, setExternalWorkOrderId] = useState('');
  const [spaceId, setSpaceId] = useState('');
  const [message, setMessage] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [executionUtc, setExecutionUtc] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState({});

  const {
    propertyId,
    groupId: storedGroupId,
    userName,
  } = useSelector(({ settings }) => ({
    propertyId: settings.propertyId,
    groupId: settings.groupId,
    userName: settings.userName,
  }));
  const groupId = useMemo(() => storedGroupId || getGroupID(), [storedGroupId]);

  const resetForm = () => {
    setSystem('Flexkeeping');
    setFacilityId('');
    setWorkOrderType('');
    setPriority(1);
    setStatus('Submitted');
    setExternalStatus('');
    setRoomTitle('');
    setExternalWorkOrderId('');
    setSpaceId('');
    setMessage('');
    setBookingId('');
    setExecutionUtc(new Date());
    setError({});
  };

  useEffect(() => {
    if (!visible) {
      resetForm();
    } else if (isEdit && item) {
      setSystem(item.system || 'Flexkeeping');
      setFacilityId(item.facilityId || '');
      setWorkOrderType(item.workOrderType || '');
      setPriority(item.priority || 1);
      setStatus(item.status || 'Submitted');
      setExternalStatus(item.externalStatus || '');
      setRoomTitle(item.roomTitle || '');
      setExternalWorkOrderId(item.externalWorkOrderId || '');
      setSpaceId(item.spaceId || '');
      setMessage(item.message || '');
      setBookingId(item.bookingId || '');
      setExecutionUtc(item.executionUtc ? new Date(item.executionUtc) : null);
    }
  }, [visible, isEdit, item]);

  const handleSave = async () => {
    let newErrors = {};
    if (!workOrderType?.trim())
      newErrors.workOrderType = 'Work order type is required';
    if (!message?.trim()) newErrors.message = 'Message is required';
    if (!facilityId?.trim()) newErrors.facilityId = 'Facility Id is required';
    if (!spaceId?.trim()) newErrors.spaceId = 'Space Id is required';

    setError(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    if (!propertyId || !groupId) {
      NotificationManager.error('Select a property to continue', 'Error', 3000);
      return;
    }

    const payload = {
      groupId,
      propertyId,
      system: toTitleCase(system),
      facilityId,
      workOrderType,
      priority: Number(priority),
      status,
      externalStatus: getStateId(status),
      roomTitle,
      externalWorkOrderId,
      spaceId,
      message,
      bookingId,
      executionUtc: executionUtc ? executionUtc.toISOString() : null,
      items: '{"tasks":[]}', // placeholder or minimal if not edited via UI
      ...(isEdit
        ? { modifiedBy: userName || 'system' }
        : { createdBy: userName || 'system' }),
    };

    setIsSaving(true);
    try {
      if (isEdit) {
        if (toTitleCase(system) == 'Flexkeeping') {
          const integrationResponse = await getChannelIntegration(
            groupId,
            system
          );
          if (integrationResponse?.status === 200) {
            if (integrationResponse?.data?.length > 0) {
              const [integration] = integrationResponse?.data;
              await AxiosAPI({
                method: 'post',
                url: `${API_Partner_Flexkeeping}/update-assignment`,
                headers: {
                  'Content-Type': 'application/json',
                  'api-Key': integration?.flexkeepingIntegration?.apiKey,
                  'User-Agent': 'integrator-a',
                },
                data: {
                  id_assignment: externalWorkOrderId,
                  id_facility: facilityId,
                  id_space: spaceId,
                  type: workOrderType,
                  id_assignment_code: null,
                  id_state: getStateId(status),
                  message,
                  priority,
                  due: executionUtc ? formatToUTC(executionUtc) : null,
                  items: [],
                },
              });

              const newPayload = {
                ...payload,
              };

              await AxiosAPI({
                method: 'put',
                url: `${API_WorkOrders}/${item.id}`,
                data: newPayload,
              });
              NotificationManager.success(
                'Work order updated successfully',
                'Success',
                3000
              );
              toggle();
              if (onSuccess) onSuccess();
            } else {
              setError({
                system:
                  'There is no integration available for this system, please configure it',
              });
            }
          }
        } else {
          await AxiosAPI({
            method: 'put',
            url: `${API_WorkOrders}/${item.id}`,
            data: payload,
          });
          NotificationManager.success(
            'Work order updated successfully',
            'Success',
            3000
          );

          toggle();
          if (onSuccess) onSuccess();
        }
      } else {
        if (toTitleCase(system) == 'Flexkeeping') {
          const integrationResponse = await getChannelIntegration(
            groupId,
            system
          );
          if (integrationResponse?.status === 200) {
            if (integrationResponse?.data?.length > 0) {
              const [integration] = integrationResponse?.data;
              const res = await AxiosAPI({
                method: 'post',
                url: `${API_Partner_Flexkeeping}/add-assignment`,
                headers: {
                  'Content-Type': 'application/json',
                  'api-Key': integration?.flexkeepingIntegration?.apiKey,
                  'User-Agent': 'integrator-a',
                },
                data: {
                  id_facility: facilityId,
                  id_space: spaceId,
                  type: workOrderType,
                  id_assignment_code: null,
                  message,
                  priority,
                  due: executionUtc ? formatToUTC(executionUtc) : null,
                  items: [],
                },
              });

              const id_assignment = res?.data?.[0]?.id_assignment;

              const newPayload = {
                ...payload,
                externalWorkOrderId: id_assignment,
                externalStatus: getStateId(status),
              };

              await AxiosAPI({
                method: 'post',
                url: API_WorkOrders,
                data: newPayload,
              });

              NotificationManager.success(
                'Work order created successfully',
                'Success',
                3000
              );
              toggle();
              if (onSuccess) onSuccess();
            } else {
              setError({
                system:
                  'There is no integration available for this system, please configure it',
              });
            }
          }
        } else {
          await AxiosAPI({
            method: 'post',
            url: API_WorkOrders,
            data: payload,
          });
          NotificationManager.success(
            'Work order created successfully',
            'Success',
            3000
          );
          toggle();
          if (onSuccess) onSuccess();
        }
      }
    } catch (err) {
      if (
        err?.response?.data?.message?.includes('Incorrect space 1 provided')
      ) {
        setError({
          spaceId: `${err?.response?.data?.message}`,
        });
      }
      NotificationManager.error(
        `Failed to ${isEdit ? 'update' : 'create'} work order`,
        'Error',
        3000
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={visible}
      toggle={toggle}
      wrapClassName="modal-right"
      backdrop="static"
    >
      <ModalHeader toggle={toggle}>
        {isEdit ? 'Edit Work Order' : 'Create Work Order'}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Colxx sm={12}>
            <Label className="mt-2">
              Work Order Type <span style={{ color: 'red' }}>*</span>
            </Label>
            <Input
              type="select"
              value={workOrderType}
              onChange={(e) => {
                setWorkOrderType(e.target.value);
                if (error.workOrderType)
                  setError({ ...error, workOrderType: '' });

                if (e.target.value == 'feedback') {
                  setSystem('default');
                } else {
                  setSystem('Flexkeeping');
                }
              }}
            >
              <option value="">Select a type...</option>
              {WORK_ORDER_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.title}
                </option>
              ))}
            </Input>
            {error.workOrderType && (
              <span style={{ color: 'red', fontSize: '12px' }}>
                {error.workOrderType}
              </span>
            )}
          </Colxx>

          <Colxx sm={12}>
            <Label className="mt-2">
              Message <span style={{ color: 'red' }}>*</span>
            </Label>
            <Input
              type="textarea"
              rows="3"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (error.message) setError({ ...error, message: '' });
              }}
              placeholder="Describe the issue or request..."
            />
            {error.message && (
              <span style={{ color: 'red', fontSize: '12px' }}>
                {error.message}
              </span>
            )}
          </Colxx>

          {isEdit && (
            <Colxx sm={6}>
              <Label className="mt-2">Status</Label>
              <Input
                type="select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Input>
            </Colxx>
          )}

          <Colxx sm={6}>
            <Label className="mt-2">Priority</Label>
            <Input
              type="select"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <option value={1}>1 - Low</option>
              <option value={2}>2 - Medium</option>
              <option value={3}>3 - High</option>
            </Input>
          </Colxx>

          <Colxx sm={6}>
            <Label className="mt-2">Execution Date</Label>
            <DatePicker
              selected={executionUtc}
              onChange={(d) => setExecutionUtc(d)}
              placeholderText="Select date & time"
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="dd/MM/yyyy h:mm aa"
              className="form-control"
            />
          </Colxx>

          <Colxx sm={6}>
            <Label className="mt-2">Room Title</Label>
            <Input
              type="text"
              value={roomTitle}
              onChange={(e) => {
                setRoomTitle(e.target.value);
                if (error.roomTitle) setError({ ...error, roomTitle: '' });
              }}
              placeholder="e.g. Room 101"
            />
            {error.roomTitle && (
              <span style={{ color: 'red', fontSize: '12px' }}>
                {error.roomTitle}
              </span>
            )}
          </Colxx>

          <Colxx sm={6}>
            <Label className="mt-2">Facility ID</Label>
            <Input
              type="text"
              value={facilityId}
              onChange={(e) => {
                setFacilityId(e.target.value);
                if (error.facilityId) setError({ ...error, facilityId: '' });
              }}
              placeholder="e.g. 1"
              readOnly={isEdit}
            />
            {error.facilityId && (
              <span style={{ color: 'red', fontSize: '12px' }}>
                {error.facilityId}
              </span>
            )}
          </Colxx>

          <Colxx sm={6}>
            <Label className="mt-2">Room ID</Label>
            <Input
              type="text"
              value={spaceId}
              onChange={(e) => {
                setSpaceId(e.target.value);
                if (error.spaceId) setError({ ...error, spaceId: '' });
              }}
              placeholder="e.g. 1"
              readOnly={isEdit}
            />
            {error.spaceId && (
              <span style={{ color: 'red', fontSize: '12px' }}>
                {error.spaceId}
              </span>
            )}
          </Colxx>

          {isEdit && (
            <Colxx sm={6}>
              <Label className="mt-2">External Work Order ID</Label>
              <Input
                type="text"
                value={externalWorkOrderId}
                onChange={(e) => setExternalWorkOrderId(e.target.value)}
                placeholder="e.g. ft2zN_PnyG5_UXhr7"
                readOnly={isEdit}
              />
            </Colxx>
          )}

          {isEdit && (
            <Colxx sm={6}>
              <Label className="mt-2">External Status</Label>
              <Input
                type="text"
                value={externalStatus}
                onChange={(e) => setExternalStatus(e.target.value)}
                placeholder="e.g. 1"
                readOnly={isEdit}
              />
            </Colxx>
          )}

          <Colxx sm={6}>
            <Label className="mt-2">Reservation ID</Label>
            <Input
              type="text"
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              placeholder="e.g. BOOK-789"
            />
          </Colxx>

          <Colxx sm={12}>
            <Label className="mt-2">System</Label>
            <Input
              type="text"
              value={system}
              onChange={(e) => {
                setSystem(e.target.value);
                if (error.system) setError({ ...error, system: '' });
              }}
              placeholder="e.g. Flexkeeping"
              readOnly={workOrderType == 'feedback' || isEdit}
            />
            {error.system && (
              <span style={{ color: 'red', fontSize: '12px' }}>
                {error.system}
              </span>
            )}
          </Colxx>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          size="xs"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Spinner size="sm" className="mr-1" /> Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
        <Button
          color="secondary"
          size="xs"
          outline
          onClick={toggle}
          disabled={isSaving}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}
